import React from 'react';
import { Link, useMatches } from '@tanstack/react-router';
import { Home } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

interface BreadcrumbSegment {
  title: string;
  path?: string | null;
  search?: Record<string, unknown>;
  isLast: boolean;
}

interface BreadcrumbsProps {
  entityName?: string;
  overrides?: Record<string, string>;
  pathOverrides?: Record<string, string | null>;
  searchOverrides?: Record<string, Record<string, unknown>>;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  entityName,
  overrides = {},
  pathOverrides = {},
  searchOverrides = {},
}) => {
  const matches = useMatches();

  const generateBreadcrumbs = (): BreadcrumbSegment[] => {
    const segments: BreadcrumbSegment[] = [];

    const currentPath = matches[matches.length - 1]?.pathname || '';
    const pathParts = currentPath.split('/').filter(Boolean);

    if (pathParts.length > 1) {
      for (let i = 1; i < pathParts.length; i++) {
        const part = pathParts[i];
        const defaultPath = `/${pathParts.slice(0, i + 1).join('/')}`;
        const fullPath = part in pathOverrides ? pathOverrides[part] : defaultPath;
        const isLast = i === pathParts.length - 1;

        let title = part;

        if (overrides[part]) {
          title = overrides[part];
        } else if (isLast && entityName && !['new'].includes(part)) {
          title = entityName;
        } else if (part === 'dashboard') {
          continue;
        } else if (part === 'new') {
          title = 'New';
        } else if (part.match(/^[0-9a-f-]{36}$/i) || !isNaN(Number(part))) {
          title = entityName || 'Details';
        } else {
          title = part.charAt(0).toUpperCase() + part.slice(1);
        }

        segments.push({
          title,
          path: !isLast ? fullPath : undefined,
          search: !isLast ? searchOverrides[part] : undefined,
          isLast,
        });
      }
    }

    return segments.map((segment, index) => ({
      ...segment,
      isLast: index === segments.length - 1,
    }));
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/dashboard" className="flex items-center gap-1">
              <Home className="h-4 w-4" />
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={`${crumb.title}-${index}`}>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {crumb.isLast || crumb.path === null ? (
                <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
              ) : (
                crumb.path && (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.path} search={crumb.search}>
                      {crumb.title}
                    </Link>
                  </BreadcrumbLink>
                )
              )}
            </BreadcrumbItem>
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
